import { BankFormState, CompanyFormState, CompanyProfile, PostalAndVisitingAddress } from "company/detail/components/models";
import PostalAddress from "company/detail/components/PostalAddress";
import { PostalAddressForm } from "company/detail/components/PostalAddressForm";
import { ProfilePicker } from "company/detail/components/ProfilePicker";
import ProfileRow from "company/detail/components/ProfileRow";
import VisitingAddress from "company/detail/components/VisitingAddress";
import { VisitingAddressForm } from "company/detail/components/VisitingAddressForm";
import { getInstruction } from "company/detail/utils";
import { Status, StatusDropdown } from "components";
import UserWithAvatar from "components/Atoms/UserWithAvatar";
import { DeletedRow } from "components/DetailPage/DeletedRow";
import LocalTimeRow from "components/DetailPage/LocalTimeRow";
import NotValidatedRow from "components/DetailPage/NotValidatedRow";
import { FormSection } from "components/MainSectionTemplate/types";
import UserModalPicker from "components/Picker/UserPicker/UserModalPicker";
import { useHasAnyTags } from "components/Tag/hooks";
import TagList from "components/Tag/TagList";
import TagsSelect from "components/Tag/TagsSelect";
import React from "react";

export const companyMainSection = (company: server.dto.Company, formState: CompanyFormState, bankFormState: BankFormState, tags: server.dto.TagSmall[]): FormSection => {
  const hasMultipleStatuses = site.currentEnvironment.statuses.company.length > 1;

  const hasAnyTags = useHasAnyTags("company");

  return {
    key: "companyMainDetails",
    type: "regular",
    title: RESX.Company.resxCompany,
    fields: [{
      key: "deleted",
      label: RESX.File.Deleted,
      hidden: !company.isDeleted,
      render: () => <DeletedRow />,
    }, {
      key: "isValidated",
      label: RESX.Planning.ExternalInput,
      render: () => <NotValidatedRow
        canApprove={company.permissions.canModify}
        sublabel={RESX.Planning.InputFromWebform}
        approveEndpoint={() => window.CoreApi.Company.Validate({
          companyId: company.companyId
        })}
      />,
      hidden: company.isValidated,
    }, {
      key: "visitingAddress",
      label: RESX.Address.resxAddress,
      render: () => <VisitingAddress company={company} />,
      inPageEdit: {
        useCustomSavingState: true,
        fullWidth: true,
        render: ({ value, onChange, submit, isSaving }) => <VisitingAddressForm
          submit={submit}
          onChange={onChange}
          value={value}
          isSaving={isSaving}
        />,
        initialValue: formState.address,
        type: "custom",
        submitChange: (value: PostalAndVisitingAddress) => {
          const instruction = getInstruction(company, formState, bankFormState, {
            regularName: value.visitingAddress.companyName,
            regularAddress: {
              addressLine1: value.visitingAddress.addressLine1,
              addressLine2: value.visitingAddress.addressLine2,
              city: value.visitingAddress.city,
              postalCode: value.visitingAddress.postalCode,
              stateId: value.visitingAddress.state.key,
            },
            administrativeName: value.postalAddress.companyName,
            administrativeAddress: {
              addressLine1: value.postalAddress.addressLine1,
              addressLine2: value.postalAddress.addressLine2,
              city: value.postalAddress.city,
              postalCode: value.postalAddress.postalCode,
              stateId: value.postalAddress.state.key,
            }
          });

          return window.CoreApi.Company.UpdateDetails(instruction);
        }
      }
    }, {
      key: "postalAddress",
      label: RESX.Company.PostalAddress,
      render: () => <PostalAddress company={company} />,
      inPageEdit: {
        useCustomSavingState: true,
        fullWidth: true,
        render: ({ value, onChange, submit, isSaving }) => <PostalAddressForm
          value={value}
          onChange={onChange}
          submit={submit}
          isSaving={isSaving}
        />,
        initialValue: formState.address,
        type: "custom",
        submitChange: (value: PostalAndVisitingAddress) => {
          const instruction = getInstruction(company, formState, bankFormState, {
            regularName: value.visitingAddress.companyName,
            regularAddress: {
              addressLine1: value.visitingAddress.addressLine1,
              addressLine2: value.visitingAddress.addressLine2,
              city: value.visitingAddress.city,
              postalCode: value.visitingAddress.postalCode,
              stateId: value.visitingAddress.state.key,
            },
            administrativeName: value.postalAddress.companyName,
            administrativeAddress: {
              addressLine1: value.postalAddress.addressLine1,
              addressLine2: value.postalAddress.addressLine2,
              city: value.postalAddress.city,
              postalCode: value.postalAddress.postalCode,
              stateId: value.postalAddress.state.key,
            }
          });

          return window.CoreApi.Company.UpdateDetails(instruction);
        }
      }
    }, {
      key: "profile",
      label: RESX.Company.Profile,
      render: () => <ProfileRow profile={formState.profile} />,
      isEmpty: formState.profile.isArtist === "no" && formState.profile.isPromoter === "no" && formState.profile.isVenue === "no",
      inPageEdit: {
        render: ({ value, onChange }) => <ProfilePicker
          onChange={onChange}
          value={value}
        />,
        type: "confirmOrCancelButton",
        initialValue: formState.profile,
        submitChange: (value: CompanyProfile) => {
          const instruction = getInstruction(company, formState, bankFormState, {
            isArtist: value.isArtist,
            isPromoter: value.isPromoter,
            isVenue: value.isVenue,
          });

          return window.CoreApi.Company.UpdateDetails(instruction);
        }

      }
    }, {
      key: "status",
      label: RESX.Status.resxStatus,
      render: () => <Status status={formState.status} />,
      hidden: !hasMultipleStatuses,
      inPageEdit: {
        render: ({ value, onChange }) => (
          <StatusDropdown
            value={value}
            onChange={onChange}
            mainArea="company"
            options={undefined}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true
            }}
          />
        ),
        type: "confirmOrCancelButton",
        initialValue: formState.status,
        fullWidth: true,
        submitChange: (value: server.dto.StatusSmall) => {
          const instruction = getInstruction(company, formState, bankFormState, {
            statusId: value.statusId,
          });

          return window.CoreApi.Company.UpdateDetails(instruction);
        }
      }
    }, {
      key: "tags",
      label: RESX.Tag.Tags,
      render: () => <TagList tags={tags} length="long" />,
      isEmpty: tags.length === 0,
      hidden: !hasAnyTags,
      inPageEdit: {
        render: ({ value, onChange }) => (
          <div className="main-section-template-fields-padding">
            <TagsSelect
              mainArea="company"
              value={value}
              onChange={onChange}
            />
          </div>
        ),
        type: "confirmOrCancelButton",
        initialValue: tags,
        submitChange: (tags: server.dto.TagSmall[]) => {
          return window.CoreApi.Company.UpdateTags({
            itemId: company.companyId,
            tagsIds: tags.map(tag => tag.tagId),
          });
        }
      }
    }, {
      key: "owner",
      label: RESX.GeneralLabels.Owner,
      render: () => <UserWithAvatar user={formState.owner} />,
      isEmpty: !formState.owner,
      inPageEdit: {
        render: ({ onChange }) => (
          <div className="main-section-template-fields-padding">
            <UserModalPicker
              startOpen
              value={undefined}
              onChange={v => onChange(v, true)}
              required={false}
              validatorKey="companyOwner"
              canCreateNew={false}
            />
          </div>
        ),
        type: "custom",
        initialValue: formState.owner,
        submitChange: (value: server.dto.UserSmall) => {
          const instruction = getInstruction(company, formState, bankFormState, {
            ownerUserId: value.userId,
          });

          return window.CoreApi.Company.UpdateDetails(instruction);
        }
      }
    }, {
      key: "localTime",
      label: RESX.DateTime.LocalTime,
      render: () => <LocalTimeRow localTime={company.localTime} timeZone={company.timeZone} />,
      isEmpty: !company.timeZone,
    },],
  }
}